import { computed, watch } from 'vue'
import { kpiMin as performanceMin, kpiMax as performanceMax, kpiAvg as performanceAvg } from './common'

// OLD CODE from: https://github.com/100-m/100m-platform/blob/cedrus/src/utils/sugar.js#L115
const rolling_window = (arr, windowsize) => arr.slice(windowsize || 1).map((d, i) => [arr[i], d])
const std_var = (series, mean) => {
  mean = mean || d3.mean(series)
  const deviation = series.map(d => (d - mean) ** 2)
  return Math.sqrt(d3.sum(deviation) / deviation.length)
}
const volatility = (series, n) => {
  const returns = rolling_window(
    series.filter(d => d != 0),
    7,
  ).map(d => d[1] / d[0] - 1)
  return std_var(returns) * Math.sqrt(n || 52) || 0
}

const isCurrent_account = isin => {
  return $root.x.mapping.find(d => d.isin == isin)?.override
}

const line_returns_movements = (isinAllocData, isinMovementsData, is_current, first_day, last_day) => {
  if (is_current) return 0

  const alloc_domain = isinAllocData.filter(d => d.date >= first_day && d.date <= last_day)
  if (alloc_domain.length === 0) return 0
  const movements_domain = isinMovementsData.filter(d => d.date > first_day && d.date <= last_day)

  const alloc_d0 = alloc_domain[0]
  const position_d0 = alloc_d0.price * alloc_d0.quantity
  const alloc_d1 = alloc_domain[alloc_domain.length - 1]
  const position_d1 = alloc_d1.price * alloc_d1.quantity

  const movements_group_by_date = movements_domain.__.group('date').__.map((v, k) => ({ date: k, delta_position: v.sum('delta_position') })).__.v()

  const alloc_position_by_date = new Map(alloc_domain.map(d => [d.date, d.position]));

  let position_before_movement = position_d0

  const ratios = movements_group_by_date.map((movement) => {
    const delta_position = movement.delta_position
    const position_after_movement = alloc_position_by_date.get(movement.date) ?? position_before_movement;
    const ratio = position_before_movement == 0 ? 1 : (position_after_movement - delta_position) / position_before_movement
    position_before_movement = position_after_movement
    return ratio
  })
  const ratio_d1 = position_before_movement == 0 ? 1 : position_d1 / position_before_movement

  return [...ratios, ratio_d1].reduce((acc, val) => acc * val, 1) - 1
}

const line_volatility = (isinContribData) => {
  if (!isinContribData || isinContribData.length === 0) return 0;
  const prices = isinContribData.filter(d => Math.abs(d.position) > 0.001).map(d => d.price)
  if (prices.length < 2) return 0;
  return volatility(prices);
}

const line_volatility_yearly = (isinContribData_X, is_current, start_date_string, target_day) => {
  if (is_current || !isinContribData_X) return 0
  const raw_data_list = isinContribData_X
    .filter(d => d.date >= start_date_string)
    .filter(d => d.position != 0 || (d.position == 0 && d.delta != 0))
    .group('date')
  const sum_list = raw_data_list.__.map((current, date) => ({ date: date, sum_position: current.sum('position'), sum_delta: current.sum('delta') }))
  var vl_day = 100
  var perf_day = 0

  const perf_list = sum_list.__.map((current, date, index) => {
    if (index == 0) {
      return { date: current.date, performance: 0, vl: vl_day }
    } else {
      perf_day = (current.sum_position - current.sum_delta) / sum_list.__.v()[index - 1].sum_position - 1
      vl_day *= 1 + perf_day
      return { date: current.date, performance: perf_day, vl: vl_day }
    }
  })
  const filtered_perf_list = perf_list.__.filter(d => d.date >= start_date_string)
    .__.filter(d => new Date(d.date).getDay() === target_day)
    .__.map(d => d.vl)
  if (filtered_perf_list.__.v().length < 2) return 0;
  const result = (
    filtered_perf_list.__.map((current, date, index) => (index == 0 ? 0 : current / filtered_perf_list.__.v()[index - 1] - 1))
      .__.v()
      .slice(1)
      .std(1) * Math.sqrt(52)
  )
  return result
}

const line_returns = (isin, first_day, last_day) => {
  if (isCurrent_account(isin) == 'True') return 0
  var trades = JSON.parse(JSON.stringify($root.x.trades.filter(d => d.isin === isin).filter(d => d.date_start <= last_day && d.date_end >= first_day)))
  if (trades.length === 0) return 0
  trades.forEach(d => {
    if (d.date_start < first_day && d.date_end != first_day)
      d.price_start = $root.xf.contribution.data.filter(d => d.isin === isin && d.date >= first_day).first().price
    if (d.date_end > last_day) d.price_end = $root.xf.contribution.data.filter(d => d.isin === isin && d.date <= last_day).last().price
    if (d.date_start < first_day && d.date_end === first_day) d.price_start = d.price_end
  })
  const pnl = trades.__.map(d => (d.price_end - d.price_start) * d.quantity).sum()
  return pnl / trades.__.map(d => d.price_start * d.quantity).sum()
}

export default function useDetailsGraphs() {
  //const action = computed(() => { return $root.action })
  //const downloadable = computed(() => { return $root.action && $root.action === 'download' })
  const downloadable = computed(() => {
    return $root.query && $root.query.action === 'download'
  })
  const first = computed(() => {
    return new Date($root.domain.first()) - new Date($root.x.contribution.first().date) > 0 ? $root.domain.first() : $root.x.contribution.first().date
  })
  const last = computed(() => {
    return new Date($root.domain.last()) - new Date($root.x.contribution.last().date) < 0 ? $root.domain.last() : $root.x.contribution.last().date
  })

  const first_day_plus_1 = computed(() => {
    const d = new Date($root.domain.first()) - new Date($root.x.contribution.first().date) > 0 ? $root.domain.first() : $root.x.contribution.first().date
    const d_next = new Date(d)
    d_next.setDate(d_next.getDate() + 1)
    return d_next.toISOString().split('T')[0]
  })

  const lastMonthFirst = computed(() => {
    const now = new Date($root.domain.last())
    var d = new Date(now.getFullYear(), now.getMonth(), 1).format()
    if (d < $root.x.performance.dates.__.keys().first()) return $root.x.performance.dates.__.keys().first()
    while (!$root.x.performance.dates.__.keys().includes(d)) {
      d = new Date(d).minus('1 day').format()
    }
    return new Date(d).format()
  })

  const lastMonthFirstBefore = computed(() => {
    const now = new Date($root.domain.last())
    var d = new Date(now.getFullYear(), now.getMonth(), 1).minus('1 day').format()
    if (d < $root.x.performance.dates.__.keys().first()) return $root.x.performance.dates.__.keys().first()
    while (!$root.x.performance.dates.__.keys().includes(d)) {
      d = new Date(d).minus('1 day').format()
    }
    return new Date(d).format()
  })

  const lastMonthFirstAfter = computed(() => {
    var d = new Date(lastMonthFirst.value).plus('2 day').format()
    while (!$root.x.performance.dates.__.keys().includes(d)) {
      if (d > $root.x.performance.dates.__.keys().last()) return $root.x.performance.dates.__.keys().last()
      d = new Date(d).plus('1 day').format()
    }
    return d
  })

  const lastMonthLast = computed(() => {
    const now = new Date($root.domain.last()).plus('1 day')
    var d = new Date(now.getFullYear(), now.getMonth(), 1).format()
    if (d < $root.x.performance.dates.__.keys().first()) return $root.x.performance.dates.__.keys().first()
    do {
      d = new Date(d).minus('1 day').format()
    } while (!$root.x.performance.dates.__.keys().includes(d))
    return new Date(d).format()
  })

  const lastMonthLastAfter = computed(() => {
    var d = new Date(lastMonthLast.value).plus('2 day').format()
    while (!$root.x.performance.dates.__.keys().includes(d)) {
      if (d > $root.x.performance.dates.__.keys().last()) return $root.x.performance.dates.__.keys().last()
      d = new Date(d).plus('1 day').format()
    }
    return d
  })

  const PreviousFiveYearsBegin = computed(() => {
    const now = new Date($root.domain.last())
    var d = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate()).format()
    if (d < $root.x.performance.dates.__.keys().first()) return $root.x.performance.dates.__.keys().first()
    while (!$root.x.performance.dates.__.keys().includes(d)) {
      d = new Date(d).minus('1 day').format()
    }
    return new Date(d).format()
  })

  const slicesFirst = computed(() => {
    return $root.x.contribution.slice($root.x.contribution.dates[lastMonthFirst.value], $root.x.contribution.dates[lastMonthFirstAfter.value])
  })

  //If today is July 26th, we take all the contributions.dates between June 30 2022 and July 2 2022 (July 2 excluded)
  const slicesLast = computed(() => {
    return $root.x.contribution.slice($root.x.contribution.dates[lastMonthLast.value], $root.x.contribution.dates[lastMonthLastAfter.value])
  })

  const slicesLastYear = computed(() => {
    return $root.x.contribution.slice(
      $root.x.contribution.dates[last.value.slice(0, 4) - 1 + '-12-31'],
      $root.x.contribution.dates[last.value.slice(0, 4) + '-01-01'],
    )
  })
  const currency_str = computed(() => $root.userflow.currency)
  const table = computed(() => {

    const first_val = first.value;
    const last_val = last.value;
    const lastMonthFirst_val = lastMonthFirst.value;
    const lastMonthFirstBefore_val = lastMonthFirstBefore.value;
    const prev5YBegin_val = PreviousFiveYearsBegin.value;

    const now = new Date($root.domain[1]);
    const yearly_start_date = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    const yearly_start_date_string = yearly_start_date.format();
    const yearly_target_day = now.getDay();

    const aum_last_val = $root.xf.performance.data.__.filter(d => d.date == $root.domain[1]).__.first().aum
    const vl0_val = $root.xf.performance.data.first().nav_before
    const currency_val = currency_str.value;

    const contribByIsin_XF = $root.xf.contribution.data.group('isin');
    const contribByIsin_X = $root.x.contribution.group('isin');
    const allocByIsin = $root.x.allocation.group('isin');
    const movementsByIsin = $root.x.movements.group('isin');
    const mappingIndex = new Map($root.x.mapping.map(d => [d.isin, d]));
    const isCurrentIndex = new Map($root.x.mapping.map(d => [d.isin, d.override == 'True']));
    const aumLastYearData = slicesLastYear.value;
    const aumLastYearIndex = new Map(aumLastYearData.map(d => [d.isin, d.position]));

    const line_benchmark_last_month = (isinContribData, is_current, lastMonthFirstDate) => {
      if (is_current || !isinContribData || isinContribData.length === 0) return 0
      const first = isinContribData.find(d => d.date === lastMonthFirstDate)?.benchmark || 1
      const last = isinContribData[isinContribData.length - 1].benchmark || 1
      return last / first - 1
    }

    const period_return_type = (type, first_day, last_day) => {
      var trades = JSON.parse(JSON.stringify($root.x.trades.filter(d => d.type === type).filter(d => d.date_start <= last_day && d.date_end >= first_day)))
      if (trades.length === 0) return 0
      trades.forEach(trade => {
        if (trade.date_start < first_day && trade.date_end != first_day)
          trade.price_start = $root.xf.contribution.data.filter(d => d.isin === trade.isin && d.date >= first_day).first().price
        if (trade.date_end > last_day) trade.price_end = $root.xf.contribution.data.filter(d => d.isin === trade.isin && d.date <= last_day).last().price
        if (trade.date_start < first_day && trade.date_end === first_day) trade.price_start = trade.price_end
      })
      const pnl = trades
        .filter(d => d.override == 'False')
        .__.map(d => (d.price_end - d.price_start) * d.quantity)
        .sum()
      return pnl / trades.__.map(d => d.price_start * d.quantity).sum()
    }

    const returns_type_last_month = (type, first_day) => {
      const data = $root.xf.contribution.data.filter(d => d.type == type).filter(d => d.date >= first_day && d.position !== 0 && d.override != 'True')
      if (data.length === 0) return 0.0
      const pnl = data.__.sum('pnl')
      return pnl / data.group('date')[data[0].date].sum('position')
    }


    const line_benchmark = (isinContribData, is_current) => {
      if (is_current || !isinContribData || isinContribData.length === 0) return 0
      const first = isinContribData[0].benchmark || 1
      const last = isinContribData[isinContribData.length - 1].benchmark || 1
      return last / first - 1
    }

    const line_pnl_latent_raw = (isin, pnl_latent_raw) => {
      return isCurrent_account(isin) == 'True' ? 0 : pnl_latent_raw
    }

    const vl0 = $root.xf.performance.data.first().nav_before
    const newCedrusOrder = [ // Request 28/09/2025 with Excel
      'liquidity',
      'poche_court_terme',
      'poche_moyen_terme',
      'poche_long_terme',
      'other',
      'liquidity',
      'term_deposit',
      'capitalization_contract',
      'poche_obligataire',
      'produits_structures',
      'poche_actions',
      'fonds_diversifies',
      'alternative_investment',
      'matieres_premieres',
      'real_estate',
      'actifs_prives',
    ]
    const previousGroupOrder = [ // Keep for legacy ptf (epf...)
      'liquidity',
      'savings_account',
      'monetary',
      'monetary_opcvm',
      'term_deposit',
      '_3_6_months',
      '_1_year',
      'bonds',
      'disintermediation',
      'capitalization_contract',
      'remunerated_account',
      'prudent',
      'monetary_stable',
      '_1_3_years',
      '_3_5_years',
      'prudent',
      'short_term_obligation',
      'diversified_balanced',
      'middle_term',
      'middle_long_term',
      '_5_years',
      'stable',
      'alternative_investment',
      'matieres_premieres',
      'ucits_equity',
      'stocks',
      'real_estate',
      'hedge_fund',
      'adossee',
      'heart',
      'diversification',
      'diversified',
      'multi_strategy',
      'fcpr',
      'diversified_real_assets',
      'side_pockets',
      'other',
      'na',
    ]

    const group_order = [...new Set([...newCedrusOrder, ...previousGroupOrder])]

    const aum_last = $root.xf.performance.data.__.filter(d => d.date == $root.domain[1]).__.first().aum

    const get_perf_by_pocket = (type, first_day, last_day) => {
      const alloc_by_type_date = $root.xf.allocation.data.filter(d => d['type'] === type && d.date >= first_day && d.date <= last_day).group('date').__.map((v, k) => v.sum('position'))
      const mvt_by_date = $root.x.movements.filter(d => d['type'] === type && d.date > first_day && d.date <= last_day).group('date').__.map((v, k) => v.sum('delta_position'))
      let position_before = $root.xf.allocation.data.filter(d => d['type'] === type && d.date === first_day).sum('position')
      let ratio_period = 1
      Object.entries(alloc_by_type_date).map(([date, position]) => {
        const mvt = mvt_by_date[date] || 0
        const ratio = (position_before === 0 || (position - mvt) === 0) ? 1 : ((position - mvt) / position_before)
        ratio_period *= ratio
        position_before = position
      })
      return ratio_period - 1
    }

    const table = $root.xf.contribution.name.__.filter(d => d != null && d.isin !== '497437A7-AFA9-49A7-B40C-661BBE47A953')
      .__.map((v, k) => {
        const isinContribData_XF = contribByIsin_XF[v.isin] || [];
        const isinContribData_X = contribByIsin_X[v.isin] || [];

        const isinAllocData = allocByIsin[v.isin] || [];
        const isinMovementsData = movementsByIsin[v.isin] || [];
        const mappingData = mappingIndex.get(v.isin) || {};
        const is_current = isCurrentIndex.get(v.isin) || false;

        const returns_val = line_returns_movements(isinAllocData, isinMovementsData, is_current, first_val, last_val)
        const returns_last_month_val = line_returns_movements(isinAllocData, isinMovementsData, is_current, lastMonthFirstBefore_val, last_val)
        const returns_5y_val = line_returns_movements(isinAllocData, isinMovementsData, is_current, prev5YBegin_val, last_val)

        const benchmark_val = line_benchmark(isinContribData_XF, is_current)
        const benchmark_last_month_val = line_benchmark_last_month(isinContribData_XF, is_current, lastMonthFirst_val)

        const aum_last_year_raw_val = aumLastYearIndex.get(v.isin) || 0;

        const volatility_val = line_volatility(isinContribData_XF)
        const volatility_yearly_val = line_volatility_yearly(isinContribData_X, is_current, yearly_start_date_string, yearly_target_day)

        const position_val = v.position_last
        const allocation_val = position_val / aum_last_val
        const pnl_val = is_current ? 0 : v.pnl
        const contribution_val = is_current ? 0 : v.pnl_wallet_shares / vl0_val
        const pnl_realised_raw_val = is_current ? 0 : v.pnl_realised
        const pnl_latent_raw_val = is_current ? 0 : v.pnl_latent
        const liquidity_val = v.liquidity
        const average_aum_val = isinContribData_XF.map(d => d.position).mean()
        return {
          name: k,
          isin: v.isin,
          type: v.type,
          manager: k === 'Liquidités' ? 'CASH' : mappingData['gerant'],
          caceis_isin: k === 'Liquidités' ? 'CASH' : mappingData['caceis_isin'],
          liquidity: liquidity_val,
          position: position_val,
          allocation: allocation_val,
          returns: returns_val,
          returns_last_month: returns_last_month_val,
          returns_5y: returns_5y_val,
          benchmark: benchmark_val,
          benchmark_last_month: benchmark_last_month_val,
          aum_last_year_raw: aum_last_year_raw_val,
          volatility: volatility_val,
          volatility_yearly: volatility_yearly_val,
          pnl: pnl_val,
          contribution: contribution_val,
          pnl_realised: v.pnl_realised,
          pnl_latent: pnl_latent_raw_val,
          pnl_realised_raw: pnl_realised_raw_val,
          pnl_latent_raw: pnl_latent_raw_val,
          average_aum: average_aum_val,
          v_ff: v,
          isr: k === 'Liquidités' ? 'CASH' : mappingData['isr'],
          zone_geo: k === 'Liquidités' ? 'CASH' : mappingData['zone_geo'],
          secteur: k === 'Liquidités' ? 'CASH' : mappingData['secteur'],
          devise: k === 'Liquidités' ? 'EUR' : mappingData['devise'],
          quantity: +v.quantity,

          liquidity_formatted: !liquidity_val ? 0 : +liquidity_val,
          allocation_formatted: !allocation_val ? 0 : format('.2%')(allocation_val),
          position_formatted: !position_val ? 0 : format('.3s' + currency_val)(position_val),
          aum_last_year_raw_formatted: !aum_last_year_raw_val ? 0 : format('.3s' + currency_val)(aum_last_year_raw_val),
          returns_last_month_formatted: !returns_last_month_val ? 0 : format('.2%')(returns_last_month_val),
          benchmark_last_month_formatted: !benchmark_last_month_val ? 0 : format('.2%')(benchmark_last_month_val),
          returns_formatted: !returns_val ? 0 : format('.2%')(returns_val),
          benchmark_formatted: !benchmark_val ? 0 : format('.2%')(benchmark_val),
          contribution_formatted: !contribution_val ? 0 : format('bp')(contribution_val),
          pnl_formatted: !pnl_val ? 0 : format('.3s' + currency_val)(pnl_val),
          volatility_yearly_formatted: !volatility_yearly_val ? 0 : format('.2%')(volatility_yearly_val),
          pnl_realised_raw_formatted: !pnl_realised_raw_val ? 0 : format('.3s' + currency_val)(pnl_realised_raw_val),
          pnl_latent_raw_formatted: !pnl_latent_raw_val ? 0 : format('.3s' + currency_val)(pnl_latent_raw_val),
        }
      })
      .__.filter(d => d.position != 0 || d.pnl != 0)
      .__.v()
      .sort([d => group_order.indexOf(d.type), 'name'])
      .group('type')
      .__.v()
      .__.map(d => {
        return {
          group: d[0].type,
          position: d.sum('position'),
          allocation: d.sum('allocation'),
          liquidity: 0,
          returns: get_perf_by_pocket(d[0].type, first_val, last_val),
          returns_last_month: get_perf_by_pocket(d[0].type, lastMonthFirstBefore_val, last_val),
          pnl: d.sum('pnl'),
          contribution: d.sum('contribution'),
          pnl_realised_raw: d.sum('pnl_realised_raw'),
          pnl_latent_raw: d.sum('pnl_latent_raw'),
          average_aum: d.sum('average_aum'),
          data: d,
        }
      })
    return table
  })

  const volatility_yearly_poche = d => {
    const now = new Date($root.domain[1])
    const start_date = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
    const start_date_string = start_date.format();
    let isins, raw_data_full
    if (!d) {
      raw_data_full = $root.x.contribution
    } else {
      isins = d.data.map('isin')
      raw_data_full = $root.x.contribution.filter(item => isins.includes(item.isin))
    }
    const raw_data_filtered = raw_data_full
      .filter(d => d.date >= start_date_string)
      .filter(d => d.position != 0 || (d.position == 0 && d.delta != 0))
    const raw_data = raw_data_filtered.group('date')
    const filtered_liq = raw_data.__.map(d => d.filter(e => e.override == 'True'))
    const sum_filtered_liq = filtered_liq.__.map((current, date, index) => {
      if (index == 0) {
        return { date: current.date, sum_delta_liq: 0 }
      } else {
        return { date: date, sum_delta_liq: current.sum('position') - filtered_liq.__.v()[index - 1].sum('position') }
      }
    })
    const sum_list = raw_data.__.map((current, date, index) => ({
      date: date,
      sum_position: current.sum('position'),
      sum_delta_others: current.filter(d => d.override == 'False').sum('delta'),
    }))
    if (sum_list.__.v().length === 0) return 0;
    var vl_day = 100
    var perf_day = 0
    const perf_list = sum_list.__.map((current, date, index) => {
      if (index == 0) {
        return { date: current.date, performance: 0, vl: vl_day }
      } else {
        const prev_position = sum_list.__.v()[index - 1].sum_position;
        if (prev_position === 0) {
          perf_day = 0;
        } else {
          perf_day =
            (current.sum_position - current.sum_delta_others - sum_filtered_liq.__.v()[index].sum_delta_liq) / prev_position - 1
        }
        vl_day *= (1 + perf_day)
        return { date: current.date, performance: perf_day, vl: vl_day }
      }
    })
    const filtered_perf_list = perf_list.__.filter(d => d.date >= start_date_string && d.date <= $root.domain[1])
      .__.filter(d => new Date(d.date).getDay() === now.getDay())
      .__.map(d => d.vl)
    if (filtered_perf_list.__.v().length < 2) return 0;
    const result = (
      filtered_perf_list.__.map((current, date, index) => (index == 0 ? 0 : current / filtered_perf_list.__.v()[index - 1] - 1))
        .__.v()
        .slice(1)
        .std(1) * Math.sqrt(52)
    )
    return result;
  }

  const table_pdf = computed(() => {
    const currency_val = currency_str.value;
    const result = table.value.reduce((acc, d) => {
      const volatility_yearly_val = volatility_yearly_poche(d)
      const benchmark_val = d.data.map('benchmark').mean()
      const benchmark_last_month_val = d.data.map('benchmark_last_month').mean()
      const aum_last_year_raw_val = d.data.sum('aum_last_year_raw')
      const group_formatted = {
        liquidity_formatted: !d.liquidity ? 0 : +d.liquidity,
        allocation_formatted: !d.allocation ? 0 : format('.2%')(d.allocation),
        position_formatted: !d.position ? 0 : format('.3s' + currency_val)(d.position),
        aum_last_year_raw_formatted: !aum_last_year_raw_val ? 0 : format('.3s' + currency_val)(aum_last_year_raw_val),
        returns_last_month_formatted: !d.returns_last_month ? 0 : format('.2%')(d.returns_last_month),
        benchmark_last_month_formatted: !benchmark_last_month_val ? 0 : format('.2%')(benchmark_last_month_val),
        returns_formatted: !d.returns ? 0 : format('.2%')(d.returns),
        benchmark_formatted: !benchmark_val ? 0 : format('.2%')(benchmark_val),
        contribution_formatted: !d.contribution ? 0 : format('bp')(d.contribution),
        pnl_formatted: !d.pnl ? 0 : format('.3s' + currency_val)(d.pnl),
        volatility_yearly_formatted: !volatility_yearly_val ? 0 : format('.2%')(volatility_yearly_val),
        pnl_realised_raw_formatted: !d.pnl_realised_raw ? 0 : format('.3s' + currency_val)(d.pnl_realised_raw),
        pnl_latent_raw_formatted: !d.pnl_latent_raw ? 0 : format('.3s' + currency_val)(d.pnl_latent_raw),
      }
      d.data.chunk(20).map(chunk => {
        acc.push({
          ...d,
          benchmark: benchmark_val,
          benchmark_last_month: benchmark_last_month_val,
          aum_last_year_raw: aum_last_year_raw_val,
          volatility_yearly: volatility_yearly_val,

          ...group_formatted,

          data: chunk,
        })
      })
      return acc
    }, [])
    return result
  })

  const allocationOptions = {
    labelKey: 'key',
    datasets: [
      {
        label: $root.t.fund,
        key: 'value',
        borderColor: 'rgb(0 145 153 / 100%)',
        backgroundColor: 'rgb(0 145 153 / 20%)',
        borderWidth: '1.5',
        fill: true,
        datalabels: true,
      },
    ],
    formatY: value => format('.3s' + $root.userflow.currency)(value),
  }
  const returnsOptions = {
    labelKey: 'key',
    datasets: [{ key: 'value', label: 'Fund', borderColor: 'none', borderRadius: '3', barThickness: '20' }],
    datalabels: false,
    labelFormatter: value => Math.round(value * 1000000) / 100 + 'bp',
    formatY: value => Math.round(value * 10000) + 'bp',
  }
  const riskOptions = line => {
    const all = table.value.find(v => v.group === line.type).data
    return {
      datasets: all.map((v, i) => {
        const color = getComputedStyle(document.body)
          .getPropertyValue(`--cat${i + 1}`)
          .trim()
        return {
          label: i + 1,
          key: 'serie-' + (i + 1),
          backgroundColor: v.isin === line.isin ? color : `rgba(0, 0, 0, 0.1)`,
          borderColor: color,
        }
      }),
      labelFormatter: value => Math.round(value * 1000000) / 100 + 'bp',
      formatX: value => Math.round(value * 10000) + 'bp',
      formatY: value => Math.round(value * 10000) + 'bp',
    }
  }
  const allocationData = line => {
    return $root.xf.contribution.data.filter(d => d.isin == line.isin).map(v => ({ key: v.date, value: v.position }))
  }

  const indexByIsin = () => {
    const allocationByIsin = {}
    const movementsByIsin = {}
    for (const a of $root.x.allocation) {
      if (!allocationByIsin[a.isin]) allocationByIsin[a.isin] = []
      allocationByIsin[a.isin].push(a)
    }
    for (const m of $root.x.movements) {
      if (!movementsByIsin[m.isin]) movementsByIsin[m.isin] = []
      movementsByIsin[m.isin].push(m)
    }
    return { allocationByIsin, movementsByIsin }
  }

  const indexByIsinComputed = computed(() => indexByIsin())

  const calculateFundPerformanceSeries = (isin, alloc, movs, dates) => {
    if (!alloc?.length) return dates.map(d => ({ date: d, fund: 0 }))
    if (isCurrent_account(isin) == 'True')
      return dates.map(d => ({ date: d, fund: 0 }))
    const allocByDate = Object.fromEntries(alloc.map(a => [a.date, a]))
    const movByDate = movs.reduce((acc, m) => {
      acc[m.date] = (acc[m.date] || 0) + m.delta_position
      return acc
    }, {})
    const results = []
    const first_date = dates[0]
    const first_alloc = allocByDate[first_date]
    if (!first_alloc) return dates.map(d => ({ date: d, fund: 0 }))
    let prev_position = first_alloc.price * first_alloc.quantity
    let total_ratio = 1
    results.push({ date: first_date, fund: 0 })
    for (let i = 1; i < dates.length; i++) {
      const date = dates[i]
      const alloc_today = allocByDate[date]
      if (!alloc_today) {
        results.push({ date, fund: total_ratio - 1 })
        continue
      }
      const position_today = alloc_today.price * alloc_today.quantity
      const delta_position = movByDate[date] || 0
      const ratio = prev_position === 0 ? 1 : (position_today - delta_position) / prev_position
      total_ratio *= ratio
      prev_position = position_today
      results.push({ date, fund: total_ratio - 1 })
    }
    return results
  }


  const performanceData = line => {
    const dates = $root.dates.filter(d => d > $root.domain[0] && d <= $root.domain[1])
    const contrib_data = $root.xf.contribution.data
      .filter(d => d.isin == line.isin && new Date(d.date) > new Date($root.domain[0]))

    const first_bench = contrib_data[0]?.benchmark || 1
    const benchmarkByDate = Object.fromEntries(contrib_data.map(c => [c.date, c.benchmark || 1]))

    const alloc = indexByIsinComputed.value.allocationByIsin[line.isin] || []
    const movs = indexByIsinComputed.value.movementsByIsin[line.isin] || []

    const fundResults = calculateFundPerformanceSeries(line.isin, alloc, movs, dates)

    const fundByDate = Object.fromEntries(fundResults.map(f => [f.date, f.fund]))

    const final_result = dates.map(d => ({
      key: d,
      fund: fundByDate[d] ?? 0,
      benchmark: (benchmarkByDate[d] || 1) / first_bench - 1,
    }))
    return final_result;
  }

  const returnsData = line => {
    // const last = $root.domain.last() || $root.x.contribution.last().date
    const groups = $root.xf.contribution.data
      .filter(d => d.isin == line.isin)
      .filter(d => new Date(d.date) > new Date($root.domain[0]))
      .group(d => new Date(d.date.slice(0, 7)).format('mon, year', $root.lang).capitalize())
    const isinAllocData = indexByIsinComputed.value.allocationByIsin[line.isin] || []
    const isinMovementsData = indexByIsinComputed.value.movementsByIsin[line.isin] || [];
    const is_current = isCurrent_account(line.isin) == 'True';
    return groups.__.reduce((acc, grp, key) => {
      acc.push({
        key,
        value: grp[0].date ? line_returns_movements(
          isinAllocData,
          isinMovementsData,
          is_current,
          grp[0].date,
          grp[grp.length - 1].date
        ) : 0
      })
      return acc
    }, [])
  }
  const riskData = line => {
    if (!line.isin) return
    const all = table.value.find(v => v.group === line.type).data
    const sizes = all.map(d => +d.position)
    const max_size = 5 / +sizes.max()
    return all.reduce((acc, d, i) => {
      acc['serie-' + (i + 1)] = [
        {
          x: +d.volatility || 0,
          y: +d.returns || 0,
          r: 3 + +d.position * max_size,
          line,
        },
      ]
      return acc
    }, {})
  }

  const export_name = 'table'
  const export_headers = [
    'type',
    'name',
    'manager',
    'liquidity',
    'allocation',
    'position',
    'aum_last_year_raw',
    'returns_last_month',
    'benchmark_last_month',
    'returns',
    'benchmark',
    'contribution',
    'pnl',
    'volatility_yearly',
    'pnl_realised_raw',
    'pnl_latent_raw',
    $root.profile.role === 'admin' && 'share_number',
    'average_aum',
  ].filter()

  const sum_columns = [
    'liquidity',
    'allocation',
    'position',
    'aum_last_year_raw',
    'returns_last_month',
    'returns',
    'benchmark',
    'benchmark_last_month',
    'contribution',
    'pnl',
    'volatility_yearly',
    'pnl_realised_raw',
    'pnl_latent_raw',
    $root.profile.role === 'admin' && 'share_number',
    'average_aum',
  ].filter()

  const export_data = computed(() => {
    const table_extend = table.value.map(t => ({ ...t, volatility_yearly: volatility_yearly_poche(t) }))
    const totals = {}
    const output = [export_headers.map(k => (k === 'benchmark' ? $root.t['benchmark_id'] : $root.t[k]))].concat(
      table_extend.flatMap(grp => {
        const header = export_headers.map(k => grp[k])
        const lines = grp.data.map(v =>
          export_headers.map(k => {
            if (k === 'share_number' && v.quantity !== false) return +v.quantity.toFixed(2)
            if (sum_columns.includes(k)) {
              if (!totals[k]) totals[k] = 0
              totals[k] += +v[k]
            }
            return v[k]
          }),
        )
        return [header].concat(lines)
      }),
    )
    // use the same 'total' logic as the pdf
    totals['liquidity'] = 0
    totals['volatility_yearly'] = volatility_yearly_poche(undefined)
    // same as getPerformance / getBenchmark in useKpis
    const contribution = $root.xf.contribution.data.filter(d => d.override == 'False')
    totals['returns'] = contribution.sum('pnl_wallet_shares') / $root.xf.performance.data.first().nav
    totals['benchmark'] =
      $root.xf.performance.data.__.filter(d => d.benchmark != 0).last().benchmark /
      $root.xf.performance.data.__.filter(d => d.benchmark != 0).first().benchmark -
      1
    const contribution_last_month = contribution.filter(d => d.date >= lastMonthFirst.value)
    totals['returns_last_month'] =
      contribution_last_month.sum('pnl_wallet_shares') / $root.xf.performance.data.find(d => d.date === lastMonthFirst.value).nav_before
    const perf_last_month = $root.xf.performance.data.__.filter(d => d.benchmark != 0).filter(d => d.date >= lastMonthFirst.value)
    totals['benchmark_last_month'] = perf_last_month.last().benchmark / perf_last_month.first().benchmark - 1

    const last_line = export_headers.map(h => (sum_columns.includes(h) ? totals[h] : ''))
    last_line[0] = $root.t['totals']
    return output.concat([last_line])
  })

  const export_csv = () => {
    const select = v => Object.fromEntries(export_headers.map((k, i) => [k, v[i]]))
    export_data.value.slice(1).map(select).dlCSV()
  }

  const export_excel = async () => {
    if (!window.XLSX) await inject('https://unpkg.com/xlsx@0.15.6/dist/xlsx.full.min.js')
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.aoa_to_sheet(export_data.value || [])
    XLSX.utils.book_append_sheet(workbook, worksheet)
    return XLSX.writeFile(workbook, export_name + '.xlsx')
  }

  watch(
    downloadable,
    downloadAction => {
      if (downloadAction) {
        export_excel()
        update_query({ action: null })
      }
    },
    { deep: true, immediate: true },
  )

  return {
    first,
    last,
    first_day_plus_1,
    lastMonthFirst,
    lastMonthFirstAfter,
    lastMonthLast,
    lastMonthLastAfter,
    PreviousFiveYearsBegin,
    slicesFirst,
    slicesLast,
    slicesLastYear,
    table,
    table_pdf,
    allocationOptions,
    allocationData,
    performanceData,
    returnsOptions,
    returnsData,
    riskOptions,
    riskData,
    export_data,
    export_csv,
    export_excel,
    performanceAvg,
    performanceMin,
    performanceMax,
    downloadable,
  }
}
